import { HttpClient } from '@angular/common/http';
import { Injectable, signal, WritableSignal } from '@angular/core';
import { SurveyCampaignItem } from '../models/SurveyCampaignItem';
import { map, Observable, of } from 'rxjs';
import Endpoints from '@utils/endpoints';
import { NewSurveyCampaign } from '@prikverwerker/models/new-survey-campaign';
import { CreateSurveyCampaign } from '@prikverwerker/models/create-survey-campaign';
import { SurveyCampaignDetail } from '@prikverwerker/models/survey-campaign-detail';
import { SoundingType } from '@prikverwerker/models/sounding-type';
import { RawSoundingData } from '@prikverwerker/models/raw-sounding-data';
import { NewRawSounding } from '@prikverwerker/models/new-raw-sounding';
import { ValidatingSoundingData } from '@prikverwerker/models/validating-sounding-data';
import { UpdateValidatedData } from '@prikverwerker/models/update-validated-sounding-data';
import { ValidatingDataDetail } from '@prikverwerker/models/validating-data-detail';
import {
  ProcessedData,
  ProcessedDataDownload,
} from '@prikverwerker/models/survey-endproduct';
import { CloseSurveyCampaign } from '@prikverwerker/models/close-survey-campaign';
import { Link } from '@prikverwerker/models/link';

@Injectable({ providedIn: 'root' })
export class ApiService {
  constructor(private readonly http: HttpClient) {}

  get closeSurveyCampaignRel() {
    return '/linkrels/detail/surveyCampaign/close';
  }

  get deleteSurveyCampaignRel() {
    return '/linkrels/overview/surveyCampaigns/delete'
  }

  get updateValidatingDataRel() {
    return '/linkrels/detail/surveyCampaign/soundings/validatingdata/update'
  }

  uploadSoundingRawDataRel(soundingTypeCode: string) {
    return `/linkrels/detail/surveyCampaign/rawdata/${soundingTypeCode}/upload`
  }

  // get uploadSB24RawDataRel() {
  //   return '/linkrels/detail/surveyCampaign/rawdata/SB24/upload'
  // }

  // get uploadSB210RawDataRel() {
  //   return '/linkrels/detail/surveyCampaign/rawdata/SB210/upload'
  // }

  // get uploadDRawDataRel() {
  //   return '/linkrels/detail/surveyCampaign/rawdata/D/upload'
  // }

  get surveyCampaignItems$(): Observable<SurveyCampaignItem[]> {
    return this.http.get<SurveyCampaignItem[]>(
      `${Endpoints.SurveyCampaigns()}`
    );
  }

  get newSurveyCampaign$(): Observable<NewSurveyCampaign> {
    return this.http.post<NewSurveyCampaign>(
      `${Endpoints.SurveyCampaigns()}`,
      {}
    );
  }

  //should be fetched from API, but I don't know the codes at front
  //to show the first list, sorting?default active code?
  get soundingTypes$(): Observable<SoundingType[]> {
    return new Observable((observer) => {
      observer.next([
        {
          code: 'SB24',
          description: 'Singlebeam 24kHz',
          active: false,
          orderNr: 2,
        },
        {
          code: 'SB210',
          description: 'Singlebeam 210kHz',
          active: true,
          orderNr: 1,
        },
        { code: 'D', description: 'Densiteit', active: false, orderNr: 3 },
      ]);
      observer.complete();
    });
  }

  triggerPostAction$(actionLink: Link) {
    return this.http.post(Endpoints.ActionLink(actionLink.uri), {});
  }

  triggerDeleteAction$(actionLink: Link) {
    return this.http.delete(Endpoints.ActionLink(actionLink.uri));
  }

  getNewRawSoundingData$(
    surveyCampaignId: string,
    soundingTypeCode: string
  ): Observable<NewRawSounding> {
    return this.http.post<NewRawSounding>(
      `${Endpoints.RawSoundingData(surveyCampaignId, soundingTypeCode)}`,
      null
    );
  }

  createSurveyCampaign$(
    createSurveyCampaign: CreateSurveyCampaign
  ): Observable<any> {
    return this.http.post<SurveyCampaignItem[]>(
      `${Endpoints.SurveyCampaigns(createSurveyCampaign.id)}`,
      createSurveyCampaign
    );
  }

  // removeSurveyCampaign$(surveyCampaignId: string): Observable<any> {
  //   return this.http.delete<void>(
  //     `${Endpoints.SurveyCampaigns(surveyCampaignId)}`
  //   );
  // }

  // closeSurveyCampaign$(surveyCampaignId: string) {
  //   return this.http.post<void>(
  //     `${Endpoints.CloseSurveyCampaign(surveyCampaignId)}`,
  //     null
  //   );
  // }

  getCloseSurveyCampaignById$(surveyCampaignId: string) {
    return this.http.get<CloseSurveyCampaign>(
      `${Endpoints.CloseSurveyCampaign(surveyCampaignId)}`
    );
  }

  getSurveyCampaignById$(id: string): Observable<SurveyCampaignDetail> {
    // TODO PRIK-138 JENS Remove pipe when api is updated
    return this.http
      .get<SurveyCampaignDetail>(`${Endpoints.SurveyCampaigns(id)}`)
      .pipe(
        map((campaign) => ({
          ...campaign,
          tideStation: campaign?.tideStation ?? 'Zeebrugge Leopold II Dam',
          tideStationLocationCode:
            campaign?.tideStationLocationCode ?? 'ZLDWS5',
        }))
      );
  }

  getRawSoundingData$(
    surveyCampaignId: string,
    activeSoundingType: SoundingType
  ): Observable<RawSoundingData[]> {
    return this.http.get<RawSoundingData[]>(
      `${Endpoints.RawSoundingData(surveyCampaignId, activeSoundingType.code)}`
    );
  }

  // Signaal dat de data opslaat
  validatingSoundingData: WritableSignal<ValidatingSoundingData[] | null> =
    signal(null);
  updateValidatingSoundingData(newData: ValidatingSoundingData[]) {
    this.validatingSoundingData.set(newData);
  }

  getValidatingSoundingData$(
    surveyCampaignId: string
  ): Observable<ValidatingSoundingData[]> {
    return this.http.get<ValidatingSoundingData[]>(
      `${Endpoints.ValidatingSoundingData(surveyCampaignId)}`
    );
  }

  updateValidatingMeasurement$(
    surveyCampaignId: string,
    updateValidatedRecord: UpdateValidatedData
  ): Observable<any> {
    return this.http.post<UpdateValidatedData>(
      `${Endpoints.ValidatingMeasurement(surveyCampaignId)}`,
      updateValidatedRecord
    );
  }

  validateMeasurements$(surveyCampaignId: string): Observable<any> {
    return this.http.post(
      `${Endpoints.ValidateMeasurements(surveyCampaignId)}`,
      null
    );
  }

  processMeasurements$(surveyCampaignId: string): Observable<any> {
    return this.http.post(
      `${Endpoints.ProcessMeasurements(surveyCampaignId)}`,
      null
    );
  }

  getValidatingDataDetail$(
    surveyCampaignId: string,
    locationId: string
  ): Observable<ValidatingDataDetail> {
    return this.http.get<ValidatingDataDetail>(
      `${Endpoints.ValidatingDataDetail(surveyCampaignId, locationId)}`
    );
  }

  getProcessedData$(surveyCampaignId: string): Observable<ProcessedData[]> {
    return this.http.get<ProcessedData[]>(
      `${Endpoints.ProcessedData(surveyCampaignId)}`
    );
  }

  deleteProcessedData$(surveyCampaignId: string): Observable<void> {
    return this.http.delete<void>(
      `${Endpoints.ProcessedData(surveyCampaignId)}`
    );
  }

  createZipProcessedData$(
    surveyCampaignId: string,
    uuids: string[]
  ): Observable<ProcessedDataDownload> {
    return this.http.post<ProcessedDataDownload>(
      `${Endpoints.ProcessedDataRequestDownloadPath(surveyCampaignId)}`,
      uuids
    );
  }
}
